import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import BpmInput from "../ui/BpmInput";
import Button from "@mui/material/Button";
import React from "react";
import Link from "../ui/RouterLink";
import {
  API_BASE,
  fetchSearch,
  SEARCH_PAGE_SIZE,
  PAGE_SIZE,
  fetchFormats,
  fetchGenres,
  fetchSubGenres,
  fetchLabels,
  fetchTags,
  fetchJson,
  resolveTrackArtists,
  resolveBpmBadge,
} from "../utils";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import FilterSection from "../components/FilterSection";
import CatalogSearch from "../components/CatalogSearch";
import SortDropdown from "../components/SortDropdown";
import useCatalogFilters from "../hooks/useCatalogFilters";
import useCatalogResults from "../hooks/useCatalogResults";
import TrashIcon from "../components/TrashIcon";
const FILTER_DEFAULTS = {
  trackQuery: "",
  trackFormat: [],
  trackGenre: [],
  trackSubGenre: [],
  trackLabel: [],
  trackTag: [],
  trackBpmMin: "",
  trackBpmMax: "",
  trackInstrumental: "",
  trackAcappella: "",
  trackStyrene: "",
  trackRanking: "",
  trackSort: "",
};
function TrackList({
  onSelect,
  view = "track",
  onViewChange,
  refreshRevision = 0,
}) {
  const [filters, setFilter] = useCatalogFilters(FILTER_DEFAULTS);
  const query = filters.trackQuery;
  const setQuery = (value) => setFilter("trackQuery", value);
  const selectedFormats = filters.trackFormat;
  const setSelectedFormats = (value) => setFilter("trackFormat", value);
  const selectedGenres = filters.trackGenre;
  const setSelectedGenres = (value) => setFilter("trackGenre", value);
  const selectedSubGenres = filters.trackSubGenre;
  const setSelectedSubGenres = (value) => setFilter("trackSubGenre", value);
  const selectedLabels = filters.trackLabel;
  const setSelectedLabels = (value) => setFilter("trackLabel", value);
  const selectedTags = filters.trackTag;
  const setSelectedTags = (value) => setFilter("trackTag", value);
  const bpmMin = filters.trackBpmMin;
  const setBpmMin = (value) => setFilter("trackBpmMin", value);
  const bpmMax = filters.trackBpmMax;
  const setBpmMax = (value) => setFilter("trackBpmMax", value);
  const instrumentalFilter = filters.trackInstrumental;
  const setInstrumentalFilter = (value) =>
    setFilter("trackInstrumental", value);
  const acappellaFilter = filters.trackAcappella;
  const setAcappellaFilter = (value) => setFilter("trackAcappella", value);
  const styreneFilter = filters.trackStyrene;
  const setStyreneFilter = (value) => setFilter("trackStyrene", value);
  const rankingFilter = filters.trackRanking;
  const setRankingFilter = (value) => setFilter("trackRanking", value);
  const sort = filters.trackSort;
  const setSort = (value) => setFilter("trackSort", value);
  const [formatOptions, setFormatOptions] = React.useState([]);
  const [genreOptions, setGenreOptions] = React.useState([]);
  const [subGenreOptions, setSubGenreOptions] = React.useState([]);
  const [labelOptions, setLabelOptions] = React.useState([]);
  const [tagOptions, setTagOptions] = React.useState([]);
  const [optionError, setOptionError] = React.useState(null);
  const [optionRevision, setOptionRevision] = React.useState(0);
  const loadMoreRef = React.useRef(null);
  const min = bpmMin === "" ? null : Number(bpmMin);
  const max = bpmMax === "" ? null : Number(bpmMax);
  const bpmError =
    (min !== null && (!Number.isFinite(min) || min < 0)) ||
    (max !== null && (!Number.isFinite(max) || max < 0))
      ? "BPM must be a finite non-negative number"
      : min !== null && max !== null && min > max
        ? "Min BPM must be ≤ Max BPM"
        : "";
  const loadPage = React.useCallback(
    async (page, term, signal) => {
      const requestFilters = {
        format: selectedFormats,
        genre: selectedGenres,
        sub_genre: selectedSubGenres,
        label: selectedLabels,
        tags: selectedTags,
        is_instrumental: instrumentalFilter,
        is_acappella: acappellaFilter,
        is_styrene: styreneFilter,
        ranking: rankingFilter,
        sort: sort,
        bpm: bpmMin || bpmMax ? `${bpmMin}-${bpmMax}` : "",
      };
      if (term) {
        const data = await fetchSearch({
          query: term,
          page,
          types: "track",
          filters: requestFilters,
          signal,
        });
        return {
          items: data.results || [],
          pageSize: SEARCH_PAGE_SIZE,
        };
      }
      const searchParams = new URLSearchParams({
        page,
        page_size: PAGE_SIZE,
      });
      Object.entries(requestFilters).forEach(([key, value]) => {
        if (Array.isArray(value))
          value.forEach((item) => searchParams.append(key, item));
        else if (value) searchParams.set(key, value);
      });
      const data = await fetchJson(
        `${API_BASE}/tracks/?${searchParams.toString()}`,
        {
          credentials: "include",
          signal,
        },
      );
      return {
        items: data.tracks || data.results || [],
        pageSize: PAGE_SIZE,
      };
    },
    [
      selectedFormats,
      selectedGenres,
      selectedSubGenres,
      selectedLabels,
      selectedTags,
      bpmMin,
      bpmMax,
      instrumentalFilter,
      acappellaFilter,
      styreneFilter,
      rankingFilter,
      sort,
    ],
  );
  const {
    items: tracks,
    hasMore,
    loading,
    fetched,
    error,
    loadMore,
    retry,
  } = useCatalogResults({
    query,
    loadPage,
    refreshRevision,
    enabled: !bpmError,
  });
  React.useEffect(() => {
    let active = true;
    setOptionError(null);
    fetchFormats()
      .then((options) => {
        if (active) setFormatOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    fetchGenres()
      .then((options) => {
        if (active) setGenreOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    fetchSubGenres()
      .then((options) => {
        if (active) setSubGenreOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    fetchLabels()
      .then((options) => {
        if (active) setLabelOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    fetchTags()
      .then((options) => {
        if (active) setTagOptions(options);
      })
      .catch((failure) => {
        if (active) setOptionError(failure);
      });
    return () => {
      active = false;
    };
  }, [optionRevision]);
  React.useEffect(() => {
    if (!loadMoreRef.current || typeof IntersectionObserver === "undefined")
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      {
        rootMargin: "0px 0px 600px 0px",
      },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [loadMore, tracks.length]);

  return (
    <div>
      {optionError && (
        <Alert role="alert" severity="error">
          Some filters could not be loaded: {optionError.message}
          <Button
            className="btn"
            onClick={() => setOptionRevision((value) => value + 1)}
          >
            Retry filters
          </Button>
        </Alert>
      )}
      {error && (
        <Alert role="alert" severity="error">
          Could not load tracks: {error.message}{" "}
          <Button type="button" className="btn" onClick={retry}>
            Retry
          </Button>
        </Alert>
      )}
      <div className="search-bar">
        <CatalogSearch
          view={view}
          onViewChange={onViewChange}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoFocus
        />
      </div>
      <FilterSection
        sortControl={
          <SortDropdown
            value={sort}
            onChange={setSort}
            options={[
              {
                value: "",
                label: "Default",
              },
              {
                value: "title",
                label: "Title A-Z",
              },
              {
                value: "-title",
                label: "Title Z-A",
              },
              {
                value: "bpm",
                label: "BPM Low-High",
              },
              {
                value: "-bpm",
                label: "BPM High-Low",
              },
            ]}
          />
        }
      >
        <div className="filter-row">
          <MultiSelectDropdown
            label="Format"
            options={formatOptions}
            selected={selectedFormats}
            onChange={setSelectedFormats}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            label="Genre"
            searchable
            options={genreOptions}
            selected={selectedGenres}
            onChange={setSelectedGenres}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            label="Sub-Genre"
            searchable
            options={subGenreOptions}
            selected={selectedSubGenres}
            onChange={setSelectedSubGenres}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            label="Label"
            searchable
            options={labelOptions}
            selected={selectedLabels}
            onChange={setSelectedLabels}
            className="filter-dropdown"
          />
          <MultiSelectDropdown
            label="Tags"
            searchable
            options={tagOptions}
            selected={selectedTags}
            onChange={setSelectedTags}
            className="filter-dropdown"
          />
          <div className="filter-bpm-range" role="group" aria-label="BPM range">
            <span>BPM</span>
            <BpmInput
              min="0"
              className={`filter-bpm-input${bpmError ? " invalid" : ""}`}
              aria-label="Minimum BPM"
              placeholder="Min"
              value={bpmMin}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || Number.isNaN(Number(val))) {
                  setBpmMin("");
                } else {
                  setBpmMin(Math.max(0, Number(val)).toString());
                }
              }}
              aria-invalid={bpmError ? "true" : undefined}
            />
            <BpmInput
              min="0"
              className={`filter-bpm-input${bpmError ? " invalid" : ""}`}
              aria-label="Maximum BPM"
              placeholder="Max"
              value={bpmMax}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || Number.isNaN(Number(val))) {
                  setBpmMax("");
                } else {
                  setBpmMax(Math.max(0, Number(val)).toString());
                }
              }}
              aria-invalid={bpmError ? "true" : undefined}
            />
          </div>
        </div>
        <div className="filter-row">
          <MultiSelectDropdown
            options={[
              {
                value: "true",
                label: "Only Instrumental",
              },
              {
                value: "false",
                label: "Not Instrumental",
              },
            ]}
            selected={instrumentalFilter ? [instrumentalFilter] : []}
            onChange={(vals) =>
              setInstrumentalFilter(vals[vals.length - 1] || "")
            }
            className="filter-dropdown"
            allLabel="Instrumental"
            showLabel={false}
            showSelected
          />
          <MultiSelectDropdown
            options={[
              {
                value: "true",
                label: "Only A cappella",
              },
              {
                value: "false",
                label: "Not A cappella",
              },
            ]}
            selected={acappellaFilter ? [acappellaFilter] : []}
            onChange={(vals) => setAcappellaFilter(vals[vals.length - 1] || "")}
            className="filter-dropdown"
            allLabel="A cappella"
            showLabel={false}
            showSelected
          />
          <MultiSelectDropdown
            options={[
              {
                value: "true",
                label: "Only Styrene",
              },
              {
                value: "false",
                label: "Not Styrene",
              },
            ]}
            selected={styreneFilter ? [styreneFilter] : []}
            onChange={(vals) => setStyreneFilter(vals[vals.length - 1] || "")}
            className="filter-dropdown"
            allLabel="Styrene"
            showLabel={false}
            showSelected
          />
          <MultiSelectDropdown
            options={[
              {
                value: "love",
                label: "❤️",
              },
              {
                value: "like",
                label: "👍",
              },
              {
                value: "hate",
                label: "❌",
              },
            ]}
            selected={rankingFilter ? [rankingFilter] : []}
            onChange={(vals) => setRankingFilter(vals[vals.length - 1] || "")}
            className="filter-dropdown"
            allLabel="Ranking"
            showLabel={false}
            showSelected
          />
        </div>
      </FilterSection>
      {bpmError && (
        <Alert className="bpm-error" role="alert" severity="error">
          {bpmError}
        </Alert>
      )}
      {(selectedFormats.length > 0 ||
        selectedGenres.length > 0 ||
        selectedSubGenres.length > 0 ||
        selectedLabels.length > 0 ||
        selectedTags.length > 0 ||
        bpmMin ||
        bpmMax ||
        instrumentalFilter ||
        acappellaFilter ||
        styreneFilter ||
        rankingFilter) && (
        <div className="applied-filters">
          {selectedFormats.map((f) => (
            <Button
              key={`f-${f}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove format ${f}`}
              onClick={() =>
                setSelectedFormats((prev) => prev.filter((x) => x !== f))
              }
            >
              {f}
              <CloseIcon />
            </Button>
          ))}
          {selectedGenres.map((g) => (
            <Button
              key={`g-${g}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove genre ${g}`}
              onClick={() =>
                setSelectedGenres((prev) => prev.filter((x) => x !== g))
              }
            >
              {g}
              <CloseIcon />
            </Button>
          ))}
          {selectedSubGenres.map((g) => (
            <Button
              key={`sg-${g}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove sub-genre ${g}`}
              onClick={() =>
                setSelectedSubGenres((prev) => prev.filter((x) => x !== g))
              }
            >
              {g}
              <CloseIcon />
            </Button>
          ))}
          {selectedLabels.map((l) => (
            <Button
              key={`l-${l}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove label ${l}`}
              onClick={() =>
                setSelectedLabels((prev) => prev.filter((x) => x !== l))
              }
            >
              {l}
              <CloseIcon />
            </Button>
          ))}
          {selectedTags.map((t) => (
            <Button
              key={`t-${t}`}
              type="button"
              className="filter-tag"
              aria-label={`Remove tag ${t}`}
              onClick={() =>
                setSelectedTags((prev) => prev.filter((x) => x !== t))
              }
            >
              {t}
              <CloseIcon />
            </Button>
          ))}
          {(bpmMin || bpmMax) && (
            <Button
              type="button"
              className="filter-tag"
              aria-label="Remove BPM filter"
              onClick={() => {
                setBpmMin("");
                setBpmMax("");
              }}
            >
              {`BPM ${bpmMin || "min"}-${bpmMax || "max"}`}
              <CloseIcon />
            </Button>
          )}
          {instrumentalFilter && (
            <Button
              type="button"
              className="filter-tag"
              aria-label="Remove instrumental filter"
              onClick={() => setInstrumentalFilter("")}
            >
              {instrumentalFilter === "true"
                ? "Only Instrumental"
                : "Not Instrumental"}{" "}
              <CloseIcon />
            </Button>
          )}
          {acappellaFilter && (
            <Button
              type="button"
              className="filter-tag"
              aria-label="Remove a cappella filter"
              onClick={() => setAcappellaFilter("")}
            >
              {acappellaFilter === "true"
                ? "Only A cappella"
                : "Not A cappella"}{" "}
              <CloseIcon />
            </Button>
          )}
          {styreneFilter && (
            <Button
              type="button"
              className="filter-tag"
              aria-label="Remove styrene filter"
              onClick={() => setStyreneFilter("")}
            >
              {styreneFilter === "true" ? "Only Styrene" : "Not Styrene"}
              <CloseIcon />
            </Button>
          )}
          {rankingFilter && (
            <Button
              type="button"
              className="filter-tag"
              aria-label="Remove ranking filter"
              onClick={() => setRankingFilter("")}
            >
              {rankingFilter === "love"
                ? "Loved"
                : rankingFilter === "like"
                  ? "Liked"
                  : "Hated"}{" "}
              <CloseIcon />
            </Button>
          )}
          <IconButton
            type="button"
            className="filter-tag clear-filters"
            aria-label="Clear all filters"
            onClick={() => {
              setSelectedFormats([]);
              setSelectedGenres([]);
              setSelectedSubGenres([]);
              setSelectedLabels([]);
              setSelectedTags([]);
              setBpmMin("");
              setBpmMax("");
              setInstrumentalFilter("");
              setAcappellaFilter("");
              setStyreneFilter("");
              setRankingFilter("");
              setSort("");
            }}
          >
            <TrashIcon />
          </IconButton>
        </div>
      )}
      <ul className="album-grid">
        {tracks.map((track) => {
          const albumId = track.album?.id ?? track.album ?? track.album_id;
          const resolvedArtists = resolveTrackArtists(track);
          const albumTitle = track.album_title || track.album?.title;
          const bpmInfo = resolveBpmBadge(
            track.bpm,
            track.default_bpm ?? track.album?.default_bpm,
          );
          return (
            <li
              key={track.id}
              className="album-tile compact-catalog-card collection-track-card"
              onClick={() => onSelect && onSelect(track)}
            >
              <div className="album-thumb">
                <img
                  src={`${API_BASE}/albums/${albumId}/image/`}
                  alt={`${track.title} artwork`}
                  loading="lazy"
                />
                {bpmInfo.hasBadge && (
                  <span
                    className={`bpm-badge ${bpmInfo.badgeClass}`.trim()}
                    aria-label={bpmInfo.isDefault ? "Default BPM" : "Track BPM"}
                    title={bpmInfo.displayValue}
                  >
                    {bpmInfo.displayValue}
                  </span>
                )}
              </div>
              <div className="album-info">
                <div className="album-title">
                  <Button
                    type="button"
                    className="catalog-title-button"
                    variant="text"
                    disableRipple
                    title={track.title}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect && onSelect(track);
                    }}
                  >
                    <span>{track.title}</span>
                  </Button>
                </div>
                {resolvedArtists.length > 0 && (
                  <div className="album-artist">
                    {resolvedArtists.map((artist, index) => (
                      <React.Fragment
                        key={artist.id ?? `${artist.name}-${index}`}
                      >
                        {artist.id ? (
                          <Link
                            className="artist-link"
                            to={`/artist/${artist.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {artist.name}
                          </Link>
                        ) : (
                          <span>{artist.name}</span>
                        )}
                        {index < resolvedArtists.length - 1 ? ", " : ""}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {!bpmInfo.hasBadge && bpmInfo.hasResolved && (
                  <div
                    className={`track-bpm-text ${bpmInfo.isDefault ? "default-bpm" : ""}`.trim()}
                  >
                    {bpmInfo.displayValue}
                  </div>
                )}
                {albumTitle && (
                  <div className="track-subtitle">{albumTitle}</div>
                )}
              </div>
            </li>
          );
        })}
        {loading && (
          <li
            className="album-tile"
            role="status"
            style={{
              textAlign: "center",
            }}
          >
            Loading...
          </li>
        )}
        {!loading && !error && fetched && tracks.length === 0 && (
          <li
            className="album-tile"
            style={{
              textAlign: "center",
            }}
          >
            No results
          </li>
        )}
        {tracks.length > 0 && hasMore && (
          <li
            ref={loadMoreRef}
            style={{
              listStyle: "none",
            }}
          />
        )}
      </ul>
      {hasMore && (
        <Button
          className="btn"
          disabled={loading || !!error}
          onClick={loadMore}
        >
          Load more
        </Button>
      )}
    </div>
  );
}
export default TrackList;
